import { useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { formatINR, formatFullINR } from "@/lib/format";
import { productIcon } from "@/lib/productMeta";
import HoldingForm from "@/components/HoldingForm";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { ChevronDown, Plus, Pencil, Trash2, Search, Upload } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" },
  { key: "with", label: "With Holdings" },
  { key: "without", label: "Without Holdings" },
];

export default function ProductHoldings({ client, products, holdings, onChange }) {
  const [expanded, setExpanded] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [formProduct, setFormProduct] = useState(null);
  const [editing, setEditing] = useState(null);
  const [importProduct, setImportProduct] = useState(null);

  const productState = client.products || {};
  const per = client.portfolio?.per_product || [];

  const holdingsByProduct = useMemo(() => {
    const m = {};
    holdings.forEach((h) => { (m[h.product_key] ||= []).push(h); });
    return m;
  }, [holdings]);

  const visible = products.filter((p) => {
    const enabled = productState[p.key]?.enabled;
    const has = (holdingsByProduct[p.key] || []).length > 0;
    if (q && !p.label.toLowerCase().includes(q.toLowerCase())) return false;
    if (filter === "active" && !enabled) return false;
    if (filter === "inactive" && enabled) return false;
    if (filter === "with" && !has) return false;
    if (filter === "without" && has) return false;
    return true;
  });

  const toggle = async (p, val) => {
    await api.put(`/clients/${client.id}/products/${p.key}`, { enabled: val });
    onChange();
  };

  const saveHolding = async (payload) => {
    try {
      if (editing) await api.put(`/holdings/${editing.id}`, payload);
      else await api.post(`/clients/${client.id}/holdings`, payload);
      toast.success("Holding saved");
      setFormProduct(null); setEditing(null);
      onChange();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const removeHolding = async (h) => {
    await api.delete(`/holdings/${h.id}`);
    toast.success("Holding deleted");
    onChange();
  };

  return (
    <div className="card-surface rounded-xl p-5">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="font-display font-semibold">Product Holdings</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Enable products and configure details to capture client holdings</p>
        </div>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="product-search-input"
            placeholder="Search product…"
            className="rounded-lg bg-secondary border border-input pl-9 pr-3 py-2 text-sm outline-none focus:border-primary transition-colors w-52"
          />
        </div>
      </div>

      <div className="flex gap-1.5 mb-4 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`product-filter-${f.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium row-hover ${filter === f.key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-2.5">
        {visible.map((p) => {
          const Icon = productIcon(p.icon);
          const enabled = productState[p.key]?.enabled;
          const recs = holdingsByProduct[p.key] || [];
          const summary = per.find((x) => x.key === p.key);
          const isOpen = expanded === p.key;
          return (
            <div key={p.key} className={`rounded-xl border row-hover ${enabled ? "border-border" : "border-border/50 opacity-80"}`} data-testid={`product-card-${p.key}`}>
              <div className="flex items-center gap-3 p-4">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${enabled ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm flex items-center gap-2">
                    {p.label}
                    {recs.length > 0 && <span className="text-xs num px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">{recs.length}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {enabled && summary
                      ? `Invested ${formatINR(summary.invested)} · Current ${formatINR(summary.current)}`
                      : p.desc}
                  </div>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium hidden sm:inline ${enabled ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"}`}>
                  {enabled ? "Enabled" : "Disabled"}
                </span>
                <Switch checked={!!enabled} onCheckedChange={(v) => toggle(p, v)} data-testid={`product-toggle-${p.key}`} />
                <button
                  onClick={() => setExpanded(isOpen ? null : p.key)}
                  data-testid={`product-expand-${p.key}`}
                  className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-secondary row-hover"
                >
                  <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
              </div>

              {isOpen && (
                <div className="px-4 pb-4 border-t border-border/50 pt-3">
                  {!enabled ? (
                    <div className="text-sm text-muted-foreground py-2">Enable this product to capture holdings.</div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {recs.map((h) => (
                          <div key={h.id} className="flex items-center gap-3 rounded-lg bg-secondary/50 px-3.5 py-2.5" data-testid={`holding-row-${h.id}`}>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium truncate">
                                {holdingTitle(p, h)}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">{holdingSub(p, h)}</div>
                            </div>
                            <button onClick={() => { setEditing(h); setFormProduct(p); }} data-testid={`holding-edit-${h.id}`} className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary row-hover">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => removeHolding(h)} data-testid={`holding-delete-${h.id}`} className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 row-hover">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                        {recs.length === 0 && <div className="text-sm text-muted-foreground py-1">No records yet.</div>}
                      </div>
                      <button
                        onClick={() => { setEditing(null); setFormProduct(p); }}
                        data-testid={`add-holding-${p.key}`}
                        className="mt-3 flex items-center gap-2 rounded-lg bg-primary/15 text-primary px-3.5 py-2 text-sm font-semibold hover:bg-primary/25 row-hover"
                      >
                        <Plus className="h-4 w-4" /> Add {p.label}
                      </button>
                      <button
                        onClick={() => setImportProduct(p)}
                        data-testid={`import-holding-${p.key}`}
                        className="mt-3 ml-2 inline-flex items-center gap-2 rounded-lg border border-border px-3.5 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary/40 row-hover"
                      >
                        <Upload className="h-4 w-4" /> Import CSV
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <HoldingForm
        open={!!formProduct}
        onOpenChange={(v) => { if (!v) { setFormProduct(null); setEditing(null); } }}
        product={formProduct}
        editing={editing}
        onSubmit={saveHolding}
      />

      <ImportDialog
        product={importProduct}
        clientId={client.id}
        onOpenChange={(v) => { if (!v) setImportProduct(null); }}
        onDone={() => { setImportProduct(null); onChange(); }}
      />
    </div>
  );
}

function parseCSV(text) {
  const rows = [];
  const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { header: [], rows: [] };
  const split = (line) => {
    const out = []; let cur = ""; let q = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') q = !q;
      else if (ch === "," && !q) { out.push(cur); cur = ""; }
      else cur += ch;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const header = split(lines[0]);
  for (let i = 1; i < lines.length; i++) {
    const cells = split(lines[i]);
    const obj = {};
    header.forEach((h, idx) => { if (cells[idx] !== undefined && cells[idx] !== "") obj[h] = cells[idx]; });
    if (Object.keys(obj).length) rows.push(obj);
  }
  return { header, rows };
}

function ImportDialog({ product, clientId, onOpenChange, onDone }) {
  const [sub, setSub] = useState(null);
  const [text, setText] = useState("");
  const open = !!product;

  const fields = product ? (product.subtypes ? product.subtypes.find((s) => s.key === (sub || product.subtypes[0].key))?.fields : product.fields) : [];
  const activeSub = product?.subtypes ? (sub || product.subtypes[0].key) : null;

  const template = () => {
    const header = fields.map((f) => f.key).join(",");
    const example = fields.map((f) => (f.type === "number" ? "0" : f.type === "date" ? "2025-01-01" : "")).join(",");
    const blob = new Blob([header + "\n" + example + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${product.key}_template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result));
    reader.readAsText(file);
  };

  const doImport = async () => {
    const { rows } = parseCSV(text);
    if (!rows.length) { toast.error("No valid rows found"); return; }
    try {
      const { data } = await api.post(`/clients/${clientId}/holdings/bulk`, {
        product_key: product.key, sub_type: activeSub, rows,
      });
      toast.success(`Imported ${data.imported} holdings`);
      setText(""); setSub(null);
      onDone();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!product) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="card-surface max-w-xl">
        <DialogHeader><DialogTitle>Import {product.label} · CSV</DialogTitle></DialogHeader>
        {product.subtypes && (
          <div className="flex gap-2">
            {product.subtypes.map((s) => (
              <button key={s.key} onClick={() => setSub(s.key)} data-testid={`import-subtype-${s.key}`}
                className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold border row-hover ${activeSub === s.key ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"}`}>
                {s.label}
              </button>
            ))}
          </div>
        )}
        <div className="text-xs text-muted-foreground">
          Columns: <span className="num">{fields.map((f) => f.key).join(", ")}</span>
        </div>
        <button onClick={template} data-testid="import-template-button" className="self-start text-xs text-primary hover:underline">Download template CSV</button>
        <input type="file" accept=".csv" onChange={onFile} data-testid="import-file-input" className="text-sm" />
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="import-csv-textarea"
          rows={6}
          placeholder="…or paste CSV content here (first row = column keys)"
          className="w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm outline-none focus:border-primary resize-none num"
        />
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="rounded-lg border border-border px-4 py-2 text-sm font-medium row-hover hover:border-primary/40">Cancel</button>
          <button onClick={doImport} data-testid="import-submit-button" className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors">Import Holdings</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fld(h, k) { return h.data?.[k]; }

function holdingTitle(p, h) {
  const d = h.data || {};
  switch (p.key) {
    case "mutual_fund": return `${d.scheme_name || d.amc || "MF"} · ${h.sub_type === "sip" ? "SIP" : "Lump Sum"}`;
    case "stock_investment": return `${d.stock_name || "Stock"} × ${d.quantity || 0}`;
    case "demat": return `${d.broker || "Demat"}${d.is_default ? " (Default)" : ""}`;
    case "insurance": return `${d.insurance_company || "Policy"} · ${d.insurance_type || ""}`;
    case "ipo": return d.ipo_name || "IPO";
    case "aif": return d.aif_fund_name || "AIF";
    case "unlisted_shares": return d.company_name || "Unlisted";
    case "nps": return `NPS · ${d.pran_number || ""}`;
    case "pms": return d.provider_name || "PMS";
    case "bonds": return d.bond_name || "Bond";
    case "fix_deposit": return `${d.institution_name || "FD"} · ${d.fd_type || ""}`;
    case "real_estate": return `${d.property_name || "Property"} · ${d.property_type || ""}`;
    case "gold": return d.asset_type || "Gold";
    case "loans": return `${d.loan_type || "Loan"} · ${d.institution || ""}`;
    default: return d.asset_name || p.label;
  }
}

function holdingSub(p, h) {
  const d = h.data || {};
  if (p.key === "mutual_fund" && h.sub_type === "sip") return `${formatFullINR(d.sip_amount)}/mo · Current ${formatFullINR(d.current_value)}`;
  if (p.key === "insurance") return `Sum Assured ${formatINR(d.sum_assured)} · Premium ${formatFullINR(d.premium_amount)}`;
  if (p.key === "demat") return `A/C ${d.demat_account_number || "—"} · ${d.account_status || ""}`;
  if (p.key === "loans") return `Outstanding ${formatFullINR(d.outstanding_amount)} · EMI ${formatFullINR(d.emi)}`;
  const invested = d.invested_amount ?? d.invested_value ?? d.investment_amount ?? d.total_investment ?? d.principal_amount ?? d.purchase_value ?? d.applied_amount ?? d.contribution_amount;
  const current = d.current_value ?? d.current_estimated_value;
  return `Invested ${formatFullINR(invested)}${current != null ? ` · Current ${formatFullINR(current)}` : ""}`;
}
